import React, { useEffect, useState } from 'react';
import { FlexBox, Input, LabelFontSize } from '@make-software/cspr-design';
import styled from 'styled-components';
import { Trans, useTranslation } from 'react-i18next';
import FilterContainer, { FilterProps } from '../filter-container';
import { BodyText } from '@block-explorer/ui';
import {
  isValidAccountHash,
  isValidCsprName,
  isValidPublicKey,
  isValidURef,
} from '../../../utils/account';
import { AccountModel } from '@block-explorer/entities';
import { useGetCsprNameInfo } from '../../../hooks/event-store-hooks';
import { useFilterQuery } from '../filter-query';

const InputContainer = styled(FlexBox)(({ theme }) =>
  theme.withMedia({
    flexDirection: ['column', 'row'],
    alignItems: 'flex-start',
    paddingBottom: 12,
    '> * + *': {
      marginLeft: [0, 24],
      marginTop: [10, 0],
    },
  })
);

const StyledBodyText = styled(BodyText)(() => ({
  cursor: 'pointer',
}));

const getPublicKeyOrHash = (value, csprNameInfo, shouldUsePublicKey) => {
  if (isValidPublicKey(value)) {
    return shouldUsePublicKey
      ? value
      : AccountModel({
          publicKeyHex: value,
        }).getAccountHash();
  }

  if (isValidCsprName(value) && csprNameInfo?.data) {
    return shouldUsePublicKey
      ? csprNameInfo.data.resolved_public_key
      : csprNameInfo.data.resolved_hash;
  }

  return value;
};

export const HashFilterWithCsprName = ({
  filterId,
  purseFilterId,
  filters,
  setFilters,
  rightLabel,
  shouldUsePublicKey = false,
  isFullView,
}: FilterProps & {
  filterId: string;
  purseFilterId?: string;
  rightLabel?: string;
  shouldUsePublicKey?: boolean;
  isFullView?: boolean;
}) => {
  const { t } = useTranslation();
  const { queryFilters, syncFiltersToQuery } = useFilterQuery();

  const queryHashFilterValue = queryFilters?.[filterId];
  const queryPurseFilterValue = queryFilters?.[purseFilterId];

  const initialHashInputValue = filters?.[filterId] || queryHashFilterValue;
  const initialPurseInputValue =
    (purseFilterId && filters?.[purseFilterId]) || queryPurseFilterValue;

  const initialInputValue = initialHashInputValue || initialPurseInputValue;
  const [inputValue, setInputValue] = useState<string>(initialInputValue);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!queryHashFilterValue && !queryPurseFilterValue) return;

    const nextInputValue = queryHashFilterValue || queryPurseFilterValue;
    if (inputValue !== nextInputValue) {
      setInputValue(nextInputValue);
      setError(null);
    }

    if (queryHashFilterValue && filters?.[filterId] !== queryHashFilterValue) {
      const { [purseFilterId ?? '']: _, ...rest } = filters ?? {};
      const newFilters = { ...rest, [filterId]: queryHashFilterValue };
      setFilters(newFilters);
    } else if (
      purseFilterId &&
      queryPurseFilterValue &&
      filters?.[purseFilterId] !== queryPurseFilterValue
    ) {
      const { [filterId ?? '']: _, ...rest } = filters ?? {};
      const newFilters = {
        ...rest,
        [purseFilterId]: queryPurseFilterValue,
      };
      setFilters(newFilters);
    }
  }, [queryHashFilterValue, queryPurseFilterValue, filterId, purseFilterId]);

  const { data: csprNameInfo, error: csprNameInfoError } = useGetCsprNameInfo(
    isValidCsprName(inputValue)
      ? {
          name: inputValue,
          includes:
            'resolved_public_key,account,account_info,centralized_account_info',
        }
      : null
  );

  const handleChangeInputValue = (text) => {
    setError(null);
    setInputValue(text);
  };

  const handleValidateInput = (inputValue: string | null) => {
    if (!inputValue) return null;
    if (
      !(
        isValidPublicKey(inputValue) ||
        isValidAccountHash(inputValue) ||
        isValidCsprName(inputValue) ||
        isValidURef(inputValue)
      )
    ) {
      return t('Value is incorrect');
    }
  };

  const handlePasteClick = async () => {
    const copiedText = await navigator.clipboard.readText();
    const error = handleValidateInput(copiedText);
    if (error) {
      setError(error);
    } else {
      setInputValue(copiedText);
    }
  };

  const handleSetFilters = () => {
    const validationError = handleValidateInput(inputValue);
    if (validationError) {
      setError(validationError);
      return;
    }

    const hash = getPublicKeyOrHash(
      inputValue,
      csprNameInfo,
      shouldUsePublicKey
    );

    const isURef = isValidURef(inputValue);
    const activeFilterId = isURef && purseFilterId ? purseFilterId : filterId;
    const oppositeFilterId = isURef ? filterId : purseFilterId;

    const { [oppositeFilterId ?? '']: _, ...rest } = filters ?? {};

    const newFilters = { ...rest, [activeFilterId]: hash };
    setFilters(newFilters);
    syncFiltersToQuery(newFilters);
  };

  const handleResetFilters = () => {
    setInputValue('');
    setError(null);

    const {
      [filterId]: _1,
      [purseFilterId ?? '']: _2,
      ...rest
    } = filters ?? {};
    setFilters(rest);
    syncFiltersToQuery(rest);
  };

  const isFilterSet = [filterId, purseFilterId].some(
    (id) => id && filters?.[id] != null
  );

  const validationText =
    error || (isValidCsprName(inputValue) && csprNameInfoError) || null;
  const disabled = !inputValue || !!validationText;

  return (
    <FilterContainer
      isFilterSet={isFilterSet}
      onSetFilters={handleSetFilters}
      onResetFilters={handleResetFilters}
      onResetErrors={() => setError(null)}
      disabled={disabled}
      isFullView={isFullView}
    >
      <InputContainer>
        <Input
          id={`${filterId}-input-filter`}
          style={{ flex: 1 }}
          onChange={(event) => {
            handleChangeInputValue(event.target.value);
          }}
          placeholder={t('Hash or CSPR.name')}
          value={inputValue}
          label={
            <BodyText size={1} scale={'sm'} variation={'black'}>
              {rightLabel || t('Filter by hash')}
            </BodyText>
          }
          rightLabel={
            <StyledBodyText
              size={1}
              variation={'blue'}
              scale={'sm'}
              onClick={handlePasteClick}
              tabIndex={0}
              onKeyDown={async (ev) => {
                if (ev.key === 'Enter') await handlePasteClick();
              }}
            >
              <Trans t={t}>Paste hash</Trans>
            </StyledBodyText>
          }
          labelFontSize={LabelFontSize.small}
          error={!!validationText}
          validationText={validationText}
          monotype
          autoFocus
        />
      </InputContainer>
    </FilterContainer>
  );
};
